import React, { useEffect, useState, useCallback } from "react";
import {
  Row,
  Col,
  Table,
  Tag,
  Button,
  Dropdown,
  Menu,
  Modal,
  Form,
  Input,
  message,
  Breadcrumb,
} from "antd";
import {
  EllipsisOutlined,
  PlusOutlined,
  ArrowLeftOutlined,
} from "@ant-design/icons";
import { useNavigate, useParams } from "react-router-dom";
import theme from "../theme";
import { getIndustry } from "../services/industries";

interface SoftwareRow {
  id: string;
  name: string;
  created_at: string;
}

interface Industry {
  id: number;
  name: string;
  color_hex: string;
}

const IndustrySoftware: React.FC = () => {
  const { industryId } = useParams();
  const navigate = useNavigate();

  const [industry, setIndustry] = useState<Industry | null>(null);
  const [rows, setRows] = useState<SoftwareRow[]>([]);
  const [loading, setLoading] = useState(false);
  const [modalOpen, setModalOpen] = useState(false);
  const [editingSoftware, setEditingSoftware] = useState<SoftwareRow | null>(null);
  const [deleting, setDeleting] = useState(false);
  const [saving, setSaving] = useState(false);
  const [toDelete, setToDelete] = useState<SoftwareRow | null>(null);
  const [form] = Form.useForm();

  const fetchData = useCallback(async () => {
    if (!industryId) return;
    setLoading(true);
    try {
      const token = localStorage.getItem("testinvest_token");
      const [industryData, softwareResponse] = await Promise.all([
        getIndustry(Number(industryId)),
        fetch(
          `${process.env.REACT_APP_API_URL}/api/software/?industry=${industryId}`,
          {
            headers: {
              Authorization: `Token ${token}`,
            },
          }
        )
      ]);

      setIndustry(industryData);

      if (softwareResponse.ok) {
        const data = await softwareResponse.json();
        setRows(
          data.map((s: any) => ({
            id: String(s.id),
            name: s.name,
            created_at: s.created_at,
          }))
        );
      }
    } catch (error) {
      message.error("Failed to fetch data");
    } finally {
      setLoading(false);
    }
  }, [industryId]);

  useEffect(() => {
    if (!industryId) return;
    fetchData();
  }, [industryId, fetchData]);

  const columns = [
    { title: "Software Name", dataIndex: "name", key: "name" },
    {
      title: "Created At",
      dataIndex: "created_at",
      key: "created_at",
      render: (date: string) => new Date(date).toLocaleDateString(),
    },
    {
      key: "actions",
      width: 60,
      render: (_: unknown, row: SoftwareRow) => (
        <Dropdown
          overlay={
            <Menu>
              <Menu.Item onClick={() => handleEdit(row)}>
                Edit
              </Menu.Item>
              <Menu.Item danger onClick={() => setToDelete(row)}>
                Delete
              </Menu.Item>
            </Menu>
          }
        >
          <Button type="text" icon={<EllipsisOutlined />} />
        </Dropdown>
      ),
    },
  ];

  const handleAdd = () => {
    setEditingSoftware(null);
    form.resetFields();
    setModalOpen(true);
  };

  const handleEdit = (softwareRow: SoftwareRow) => {
    setEditingSoftware(softwareRow);
    form.setFieldsValue({ name: softwareRow.name });
    setModalOpen(true);
  };

  const handleSave = async (values: { name: string }) => {
    if (!industryId) return;

    setSaving(true);
    try {
      const token = localStorage.getItem("testinvest_token");
      const url = editingSoftware
        ? `${process.env.REACT_APP_API_URL}/api/software/${editingSoftware.id}/`
        : `${process.env.REACT_APP_API_URL}/api/software/`;

      const response = await fetch(url, {
        method: editingSoftware ? "PATCH" : "POST",
        headers: {
          Authorization: `Token ${token}`,
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          name: values.name,
          industry: Number(industryId),
        }),
      });

      if (response.ok) {
        message.success(
          editingSoftware ? "Software updated successfully" : "Software created successfully"
        );
        setModalOpen(false);
        fetchData();
      } else {
        message.error("Failed to save software");
      }
    } catch (error) {
      message.error("Failed to save software");
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!toDelete) return;

    setDeleting(true);
    try {
      const token = localStorage.getItem("testinvest_token");
      const response = await fetch(
        `${process.env.REACT_APP_API_URL}/api/software/${toDelete.id}/`,
        {
          method: "DELETE",
          headers: {
            Authorization: `Token ${token}`,
          },
        }
      );

      if (response.ok) {
        message.success("Software deleted successfully");
        setToDelete(null);
        fetchData();
      } else {
        message.error("Failed to delete software");
      }
    } catch (error) {
      message.error("Failed to delete software");
    } finally {
      setDeleting(false);
    }
  };

  return (
    <div style={bg}>
      <Breadcrumb style={{ marginBottom: 16 }}>
        <Breadcrumb.Item>
          <Button
            type="link"
            onClick={() => navigate("/industries-list")}
            style={{ padding: 0, height: 'auto' }}
          >
            Industries
          </Button>
        </Breadcrumb.Item>
        <Breadcrumb.Item>
          <Button
            type="link"
            onClick={() => navigate(`/view-industry/${industryId}`)}
            style={{ padding: 0, height: 'auto' }}
          >
            {industry?.name || "Loading..."}
          </Button>
        </Breadcrumb.Item>
      </Breadcrumb>

      <Row justify="space-between" align="middle" style={{ marginBottom: 24 }}>
        <Col>
          <h1 style={heading}>Software</h1>
        </Col>

        <Col>
          <Button
            type="primary"
            icon={<PlusOutlined />}
            style={createBtn}
            onClick={handleAdd}
          >
            Add new
          </Button>
        </Col>
      </Row>

      <div style={card}>
        <Table
          columns={columns}
          dataSource={rows}
          rowKey="id"
          loading={loading}
          pagination={false}
          bordered
          locale={{
            emptyText: (
              <div style={emptyStateContainer}>
                <div style={emptyStateText}>No software found</div>
                <div style={emptyStateSubtext}>Create your first software to get started</div>
              </div>
            )
          }}
        />
      </div>

      {/* Add/Edit Modal */}
      <Modal
        open={modalOpen}
        title={editingSoftware ? "Edit Software" : "Add New Software"}
        okText="Save"
        okButtonProps={{ loading: saving }}
        cancelButtonProps={{ disabled: saving }}
        onOk={() => form.submit()}
        onCancel={() => setModalOpen(false)}
      >
        <Form
          form={form}
          layout="vertical"
          onFinish={handleSave}
        >
          <Form.Item
            name="name"
            label="Software Name"
            rules={[{ required: true, message: "Please enter software name" }]}
          >
            <Input placeholder="Enter software name" />
          </Form.Item>
        </Form>
      </Modal>

      {/* Delete Modal */}
      <Modal
        open={!!toDelete}
        title="Delete Software?"
        okText="Delete"
        okButtonProps={{ danger: true, loading: deleting }}
        cancelButtonProps={{ disabled: deleting }}
        onOk={handleDelete}
        onCancel={() => setToDelete(null)}
      >
        Are you sure you want to delete "{toDelete?.name}"?
      </Modal>
    </div>
  );
};

const bg: React.CSSProperties = {
  minHeight: "100vh",
};

const heading: React.CSSProperties = {
  fontSize: 28,
  fontWeight: 700,
  color: theme.colors.primary,
  margin: 0,
};



const card: React.CSSProperties = {
  background: "#ffffff",
  borderRadius: 8
};

const createBtn: React.CSSProperties = {
  background: theme.colors.primary,
  borderRadius: 9999,
  border: "none",
  padding: "0 24px",
  fontWeight: 600,
};

const emptyStateContainer: React.CSSProperties = {
  padding: "40px",
  textAlign: "center",
};

const emptyStateText: React.CSSProperties = {
  fontSize: "16px",
  color: "#666",
  marginBottom: "8px",
  fontWeight: 500,
};

const emptyStateSubtext: React.CSSProperties = {
  fontSize: "14px",
  color: "#999",
};

export default IndustrySoftware;